/**
 * @file NotificationLogTable.tsx
 * @description Вкладка «Журнал» страницы «Уведомления» (phase19): таблица
 * NotificationLog с фильтрами канал/статус и пагинацией.
 */

import { useEffect, useState } from "react";

import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import {
  listNotificationLog,
  LOG_SKIP_REASON_LABELS,
  LOG_STATUS_LABELS,
  type NotificationLogItem,
  type NotificationLogStatus,
} from "../../../../lib/api-notification-log";
import {
  NOTIFICATION_CHANNEL_LABELS,
  NOTIFICATION_KIND_LABELS,
  type NotificationChannel,
} from "../../../../lib/api-notification-templates";
import { formatLogDate, TabEmpty, TabStateBlock, tabErrorMessage } from "../NotificationLogPage";

const PAGE_SIZE = 25;

const STATUS_TONE: Record<NotificationLogStatus, "primary" | "secondary" | "outline"> = {
  SENT: "primary",
  MOCK: "secondary",
  QUEUED_RETRY: "outline",
  SKIPPED: "outline",
};

export function NotificationLogTable() {
  const [items, setItems] = useState<NotificationLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState<NotificationChannel | undefined>();
  const [status, setStatus] = useState<NotificationLogStatus | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listNotificationLog({ channel, status, page, limit: PAGE_SIZE })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => setError(tabErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [channel, status, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-sm text-on-surface-variant">Канал:</span>
        {([undefined, "EMAIL", "SMS", "TELEGRAM", "IN_APP"] as const).map((c) => (
          <Button
            key={c ?? "all"}
            variant={channel === c ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setChannel(c);
              setPage(1);
            }}
          >
            {c ? NOTIFICATION_CHANNEL_LABELS[c] : "Все"}
          </Button>
        ))}
        <span className="ml-3 mr-1 text-sm text-on-surface-variant">Статус:</span>
        {([undefined, "SENT", "MOCK", "QUEUED_RETRY", "SKIPPED"] as const).map((s) => (
          <Button
            key={s ?? "all"}
            variant={status === s ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {s ? LOG_STATUS_LABELS[s] : "Все"}
          </Button>
        ))}
      </div>

      <TabStateBlock loading={loading} error={error} />
      {!loading && !error && items.length === 0 && (
        <TabEmpty text="Записей нет — уведомления ещё не отправлялись." />
      )}

      {!loading && !error && items.length > 0 && (
        <EditorialCard>
          <EditorialCard.Body className="!p-0 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-on-surface/5 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Когда</th>
                  <th className="px-4 py-3 font-semibold">Событие</th>
                  <th className="px-4 py-3 font-semibold">Канал</th>
                  <th className="px-4 py-3 font-semibold">Получатель</th>
                  <th className="px-4 py-3 font-semibold">Клиент</th>
                  <th className="px-4 py-3 font-semibold">Заказ</th>
                  <th className="px-4 py-3 font-semibold">Статус</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-on-surface/8">
                    <td className="whitespace-nowrap px-4 py-2.5 text-on-surface-variant">
                      {formatLogDate(it.createdAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      {NOTIFICATION_KIND_LABELS[it.kind]}
                      {it.subject ? (
                        <div className="max-w-[280px] truncate text-xs text-on-surface-variant">
                          {it.subject}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5">{NOTIFICATION_CHANNEL_LABELS[it.channel]}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">
                      {it.recipient ?? "—"}
                    </td>
                    <td className="px-4 py-2.5">{it.customer?.displayName ?? "—"}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">
                      {it.order?.number ?? "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={STATUS_TONE[it.status]}>
                        {LOG_STATUS_LABELS[it.status]}
                      </Badge>
                      {it.skipReason ? (
                        <div className="mt-0.5 text-xs text-on-surface-variant">
                          {LOG_SKIP_REASON_LABELS[it.skipReason] ?? it.skipReason}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </EditorialCard.Body>
        </EditorialCard>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Назад
          </Button>
          <span className="text-on-surface-variant">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Вперёд
          </Button>
        </div>
      )}
    </div>
  );
}
